// eslint-disable-next-line jira/restricted/react-component-props
import React, { forwardRef, type ReactElement, type ComponentProps, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { type ButtonProps } from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import AkTooltip from '@atlaskit/tooltip';
import type AsyncIcon from '@atlassian/jira-common-components-async-icon/src/view.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TriggerButtonContainerVR = styled.div({
	font: token('font.body'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const TriggerButtonContainer = styled.div({
	paddingRight: token('space.050', '4px'),
	font: token('font.body'),
});

type Props = {
	issueType: string;
	icon: ReactElement<ComponentProps<typeof AsyncIcon>>;
	onClick: (event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
} & ButtonProps;

const TriggerButton = forwardRef<HTMLAnchorElement, Props>(
	({ issueType, icon, onClick, ...props }, ref) => {
		const { formatMessage } = useIntl();

		return isVisualRefreshEnabled() && fg('jira_nav4_eap_drop_2') ? (
			<TriggerButtonContainerVR>
				<AkTooltip
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					testId="issue.views.issue-base.foundation.change-issue-type.tooltip"
					position="top"
					content={formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.changeTypeIssueTermRefresh
							: messages.changeType,
						{
							issueType,
						},
					)}
					delay={0}
				>
					<Button
						{...props}
						/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
						testId="issue.views.issue-base.foundation.change-issue-type.button"
						aria-label={formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.changeTypeIssueTermRefresh
								: messages.changeType,
							{
								issueType,
							},
						)}
						spacing="compact"
						appearance="subtle"
						onClick={onClick}
						iconBefore={icon}
						ref={ref}
						interactionName="auto-generated.issue.views.issue-base.foundation.change-issue-type.button.button"
					/>
				</AkTooltip>
			</TriggerButtonContainerVR>
		) : (
			<TriggerButtonContainer>
				<AkTooltip
					/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
					testId="issue.views.issue-base.foundation.change-issue-type.tooltip"
					position="top"
					content={formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.changeTypeIssueTermRefresh
							: messages.changeType,
						{
							issueType,
						},
					)}
					delay={0}
				>
					<Button
						{...props}
						/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
						testId="issue.views.issue-base.foundation.change-issue-type.button"
						aria-label={formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.changeTypeIssueTermRefresh
								: messages.changeType,
							{
								issueType,
							},
						)}
						spacing="compact"
						appearance="subtle"
						onClick={onClick}
						iconBefore={icon}
						ref={ref}
						interactionName="auto-generated.issue.views.issue-base.foundation.change-issue-type.button.button"
					/>
				</AkTooltip>
			</TriggerButtonContainer>
		);
	},
);

export default TriggerButton;
